import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { CheckCircle2, Repeat, UserPlus, Users } from "lucide-react"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import { IconTile, Panel, StatCard, StatGrid } from "@/components/superadmin/kit"
import { useCondominiums } from "@/hooks/useCondominiums"
import { useProfiles } from "@/hooks/useProfiles"
import { useSuperAdminStats } from "@/hooks/useSuperAdminStats"
import { supabase } from "@/integrations/supabase/client"
import { ALL_MODULES, MODULE_META, moduleIncluded, resolveOrgModules } from "@/lib/modules"
import { fmtCurrency, propostaFromRow } from "@/lib/propostas"

import { MODULE_ICON, MODULE_TONE } from "./ModulePicker"
import { ROLE_META } from "./UserManagement"

// Sequential blues + neutral: one hue family instead of a rainbow.
const ROLE_COLORS = ["hsl(224 100% 64%)", "hsl(224 85% 50%)", "hsl(224 60% 38%)", "hsl(224 40% 28%)", "hsl(228 8% 26%)"]
const MODULE_BAR = { full_itacondo: "hsl(224 100% 66%)", access_control: "hsl(258 90% 72%)", time_clock: "hsl(152 60% 52%)" } as const

const SuperAdminAnalytics = () => {
  const { data: stats } = useSuperAdminStats()
  const { data: profiles = [] } = useProfiles()
  const { data: condominiums = [] } = useCondominiums()
  const { data: proposals = [] } = useQuery({
    queryKey: ["superadmin-analytics-proposals"],
    queryFn: async () => {
      const { data, error } = await supabase.from("propostas").select("*")
      if (error) throw error
      return (data || []).map(propostaFromRow)
    },
  })

  const accepted = proposals.filter((p) => p.status === "aceita")
  const acceptedValue = accepted.reduce((t, p) => t + p.valorFinal, 0)
  const recurring = accepted.reduce((t, p) => {
    const m = p.modalidades?.find((x) => x.id === p.modalidadeEscolhida)
    return t + (m?.valorMensal ?? 0)
  }, 0)

  const monthly = useMemo(() => {
    const now = new Date()
    return Array.from({ length: 8 }, (_, i) => {
      const start = new Date(now.getFullYear(), now.getMonth() - (7 - i), 1)
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 1)
      return {
        label: start.toLocaleDateString("pt-BR", { month: "short" }).replace(".", ""),
        users: profiles.filter((p) => p.created_at && new Date(p.created_at) >= start && new Date(p.created_at) < end).length,
      }
    })
  }, [profiles])

  const roles = Object.entries(
    profiles.reduce<Record<string, number>>((acc, p) => {
      acc[p.role] = (acc[p.role] || 0) + 1
      return acc
    }, {}),
  ).sort((a, b) => b[1] - a[1])
  const totalRoles = Math.max(profiles.length, 1)

  // Suíte completa conta em todos os módulos; "só" = organizações com apenas aquele módulo.
  const byModule = useMemo(() => {
    const usersByOrg = new Map<string, number>()
    for (const p of profiles) if (p.condominium_id) usersByOrg.set(p.condominium_id, (usersByOrg.get(p.condominium_id) ?? 0) + 1)
    return ALL_MODULES.map((m) => {
      const orgs = condominiums.filter((c) => moduleIncluded(resolveOrgModules(c), m))
      return {
        module: m,
        total: orgs.length,
        active: orgs.filter((c) => c.payment_status === "active").length,
        exclusive: orgs.filter((c) => { const mods = resolveOrgModules(c); return mods.length === 1 && mods[0] === m }).length,
        users: orgs.reduce((t, c) => t + (usersByOrg.get(c.id) ?? 0), 0),
      }
    })
  }, [condominiums, profiles])
  const totalOrgs = Math.max(condominiums.length, 1)

  const finance: [string, number][] = [
    ["Organizações ativas", stats?.active_condominiums || 0],
    ["Pagamentos pendentes", stats?.pending_payments || 0],
    ["Pagamentos em atraso", stats?.overdue_condominiums || 0],
    ["Contas suspensas", stats?.suspended_condominiums || 0],
    ["Vencendo em 30 dias", stats?.expiring_soon || 0],
  ]

  return (
    <>
      <StatGrid>
        <StatCard label="Novos usuários (30 dias)" value={stats?.new_users_month || 0} icon={UserPlus} tone="accent" />
        <StatCard label="Usuários ativos" value={stats?.active_users || 0} icon={Users} />
        <StatCard label="Recorrência contratada" value={fmtCurrency(recurring)} hint="Mensalidades das modalidades aceitas" icon={Repeat} tone="violet" />
        <StatCard label="Propostas aceitas" value={fmtCurrency(acceptedValue)} hint={`${accepted.length} aceita${accepted.length === 1 ? "" : "s"}`} icon={CheckCircle2} tone="success" />
      </StatGrid>

      <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
        <Panel title="Novos usuários por mês" description="Últimos 8 meses" bodyClassName="px-2 pb-2 pt-4">
          <div className="h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="hsl(228 8% 14%)" />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "hsl(225 6% 50%)", fontSize: 11 }} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "hsl(225 6% 50%)", fontSize: 11 }} />
                <Tooltip
                  cursor={{ fill: "hsl(228 8% 12%)" }}
                  contentStyle={{ background: "hsl(228 9% 9%)", border: "1px solid hsl(228 8% 17%)", borderRadius: 8, color: "hsl(225 10% 93%)", fontSize: 12 }}
                  labelStyle={{ color: "hsl(225 6% 60%)" }}
                  formatter={(v: number) => [v, "Novos usuários"]}
                />
                <Bar dataKey="users" fill="hsl(224 100% 64%)" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Situação financeira" flush>
          <ul className="divide-y divide-border">
            {finance.map(([label, value]) => (
              <li key={label} className="flex items-center justify-between px-4 py-3 text-[13px]">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-semibold tabular-nums text-foreground">{value}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel title="Organizações por módulo" description={`${condominiums.length} organizações · a suíte completa conta em todos os módulos`} flush>
        <ul className="divide-y divide-border">
          {byModule.map((row) => (
            <li key={row.module} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <IconTile icon={MODULE_ICON[row.module]} tone={MODULE_TONE[row.module]} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-foreground">{MODULE_META[row.module].label}</div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[hsl(228_8%_12%)]">
                    <span className="block h-full rounded-full" style={{ width: `${(row.total / totalOrgs) * 100}%`, background: MODULE_BAR[row.module] }} />
                  </div>
                </div>
              </div>
              <dl className="grid grid-cols-4 gap-4 text-right text-[12px] sm:w-[340px]">
                {([["Total", row.total], ["Em dia", row.active], ["Só este", row.exclusive], ["Usuários", row.users]] as const).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="text-[14px] font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Distribuição de perfis" description={`${profiles.length} usuários`}>
        <div className="flex h-2 overflow-hidden rounded-full bg-[hsl(228_8%_12%)]">
          {roles.map(([role, count], i) => (
            <span key={role} style={{ width: `${(count / totalRoles) * 100}%`, background: ROLE_COLORS[i % ROLE_COLORS.length] }} />
          ))}
        </div>
        <ul className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-5">
          {roles.map(([role, count], i) => (
            <li key={role} className="flex items-center gap-2 text-[13px]">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: ROLE_COLORS[i % ROLE_COLORS.length] }} />
              <span className="flex-1 text-muted-foreground">{ROLE_META[role]?.label ?? role}</span>
              <span className="tabular-nums text-foreground">{count}</span>
              <span className="w-10 text-right text-[11px] tabular-nums text-muted-foreground">{Math.round((count / totalRoles) * 100)}%</span>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  )
}

export { SuperAdminAnalytics }
